import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, switchMap, take, timeout } from 'rxjs/operators';
import { interval } from 'rxjs';
import { NMI_CONFIG, NmiConfig } from '../../nmi.config';
import {
  AsyncStatusResponse,
  PosConfirmRequest,
  PosDevice,
  PosSaleRequest,
  PosSaleResponse,
  RegisterDeviceRequest,
  UpdateDeviceRequest,
} from '../../models/nmi.models';

@Injectable()
export class NmiPosPaymentService {

  private readonly base: string;

  constructor(
    private http: HttpClient,
    @Inject(NMI_CONFIG) config: NmiConfig
  ) {
    this.base = config.apiUrl.replace(/\/$/, '');
  }

  // ─── Device management ───────────────────────────────────────────────────────
  // clinicId is NOT sent — the backend derives it from the JWT auth context (always cmb_-prefixed).

  getDevices(): Observable<PosDevice[]> {
    return this.http.get<PosDevice[]>(`${this.base}/api/pos/devices`);
  }

  registerDevice(req: RegisterDeviceRequest): Observable<PosDevice> {
    return this.http.post<PosDevice>(`${this.base}/api/pos/devices/register`, req);
  }

  deregisterDevice(poiDeviceId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/pos/devices/${encodeURIComponent(poiDeviceId)}`
    );
  }

  updateDevice(req: UpdateDeviceRequest): Observable<PosDevice> {
    return this.http.patch<PosDevice>(
      `${this.base}/api/pos/devices/${encodeURIComponent(req.poi_device_id)}`,
      req
    );
  }

  // ─── Transactions ────────────────────────────────────────────────────────────

  processSale(req: PosSaleRequest): Observable<PosSaleResponse> {
    return this.http.post<PosSaleResponse>(`${this.base}/api/pos/transactions/sale`, req);
  }

  confirmPayment(req: PosConfirmRequest): Observable<{ confirmed: boolean; payment_id?: number }> {
    return this.http.post<{ confirmed: boolean; payment_id?: number }>(
      `${this.base}/api/pos/transactions/confirm`, req
    );
  }

  getAsyncStatus(asyncGuid: string): Observable<AsyncStatusResponse> {
    return this.http.get<AsyncStatusResponse>(
      `${this.base}/api/pos/transactions/status/${encodeURIComponent(asyncGuid)}`
    );
  }

  /**
   * Polls getAsyncStatus every 3 seconds until asyncStatus !== 'inFlight'.
   * Completes with the final status. Throws TimeoutError after 5 minutes.
   */
  pollUntilComplete(asyncGuid: string): Observable<AsyncStatusResponse> {
    return interval(3000).pipe(
      switchMap(() => this.getAsyncStatus(asyncGuid)),
      filter(r => r.async_status === 'interactionComplete'
                || r.async_status === 'cancelledAtTerminal'
                || r.async_status === 'error'),
      take(1),
      timeout(300000)
    );
  }
}
